import React from 'react';
import type { ContactIdentityEventInfo, ContactIdentityInfo } from '../../services/CardService';

interface Props {
  identities: ContactIdentityInfo[];
  events: ContactIdentityEventInfo[];
  /** Abre a ficha do outro cliente quando um identificador também está nele. */
  onOpenContact?: (contactId: number) => void;
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "visto no canal" (o cliente escreveu por ele) ou quem informou o identificador. */
export function identityTag(i: Pick<ContactIdentityInfo, 'status' | 'source'>): string {
  if (i.status === 'declared') return i.source === 'user_stated' ? 'informado pela equipe' : 'informado pelo cliente';
  return 'visto no canal';
}

function eventPhrase(e: ContactIdentityEventInfo): string {
  switch (e.kind) {
    case 'observed':
      return 'Canal visto pela primeira vez';
    case 'verified':
      return 'Confirmado: o cliente escreveu por este canal';
    case 'declared': {
      const who = e.actor === 'user' ? 'Informado pela equipe' : 'Informado pelo cliente';
      return e.quote ? `${who}: “${e.quote}”` : who;
    }
    case 'declared_conflict':
      return `Também está em outro cliente: ${e.otherContactName ?? 'cliente'} (#${e.otherContactId ?? '?'})`;
    case 'observed_conflict':
      return 'Mensagem deste canal pertence a outro cliente';
    case 'retired':
      return 'Identificador retirado: o cadastro do cliente mudou';
    case 'reassigned':
      return 'Identificador que era de um cliente apagado passou para este cliente';
    default:
      return e.kind;
  }
}

/**
 * Canais conhecidos do cliente (WhatsApp, e-mail, Messenger, Instagram), com a etiqueta de como cada um foi descoberto, e
 * a trilha de cada vínculo (quem viu, quem informou e os conflitos com outro cliente).
 */
const ContactIdentityList: React.FC<Props> = ({ identities, events, onOpenContact }) => (
  <div className="identity-list">
    <ul className="identity-list__items" data-testid="contact-identities">
      {identities.map(i => (
        <li key={i.id} className="identity-list__item">
          <strong>{i.channelLabel}</strong>{' '}
          {i.value ? <span className="identity-list__value">{i.value}</span> : null}
          {i.handle ? <span className="identity-list__value"> @{i.handle}</span> : null}
          <span className="opp-panel__muted"> · {identityTag(i)}</span>
        </li>
      ))}
    </ul>
    {events.length > 0 && (
      <details className="identity-list__history" data-testid="identity-history">
        <summary>Histórico de identidade</summary>
        <ul className="identity-list__events">
          {events.map(e => (
            <li key={e.id}>
              <div>
                {e.channel && e.value ? <span className="opp-panel__muted">{e.value} · </span> : null}
                {eventPhrase(e)}
                {e.kind === 'declared_conflict' && e.otherContactId != null && onOpenContact ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="opp-panel__link"
                      aria-label={`Abrir ficha de ${e.otherContactName ?? 'cliente'}`}
                      onClick={() => onOpenContact(e.otherContactId as number)}
                    >
                      Abrir ficha
                    </button>
                  </>
                ) : null}
              </div>
              <div className="opp-panel__muted">{formatWhen(e.createdAtUtc)}</div>
            </li>
          ))}
        </ul>
      </details>
    )}
  </div>
);

export default ContactIdentityList;
